import { CurrencyPipe, PercentPipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, inject } from "@angular/core";
import { BillingPlan, Currency, DailyAdSpendFee, Marketplace } from "@front/m19-api-client";
import { ITableImports, MODAL_DATA } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { CsvExportService, fieldExtractor } from "@m19-board/services/csv-export.service";
import { ExportButtonComponent } from "@m19-board/shared/ui/export-buttons/export-button.component";

export interface BillableAdSpendDetailModalData {
  billingPlan?: BillingPlan;
  currency: Currency;
  exportFilePrefix: string;
  locale?: string;
  adSpendFees: DailyAdSpendFee[];
  dspAdSpendFees: DailyAdSpendFee[];
  dspM19SeatAdSpendFees: DailyAdSpendFee[];
}

export interface AdSpendFeeRow {
  name: string;
  marketplace: Marketplace;
  adSpend: number;
  adSpendFee: number;
}

export interface AdSpendFeeSection {
  titleKey: string;
  nameColumnKey: string;
  feeRate?: number;
  rows: AdSpendFeeRow[];
  adSpendSubtotal: number;
  adSpendFeeSubtotal: number;
}

interface AdSpendFeeCsvLine extends AdSpendFeeRow {
  section: string;
}

@Component({
  templateUrl: "./billable-ad-spend-detail-modal.component.html",
  imports: [TranslocoDirective, CurrencyPipe, PercentPipe, ITableImports, ExportButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillableAdSpendDetailModalComponent {
  private readonly translocoService = inject(TranslocoService);
  private readonly csvExportService = inject(CsvExportService);
  readonly data = inject<BillableAdSpendDetailModalData>(MODAL_DATA);

  readonly sections: AdSpendFeeSection[] = [
    toSection(
      "billable-ad-spend-detail-modal.sponsored_ads",
      "common.account",
      this.data.billingPlan?.adSpendFee?.amount,
      this.data.adSpendFees,
      (fee) => `${fee.accountId}_${fee.marketplace}`,
      (fee) => fee.accountName || fee.accountId!,
    ),
    toSection(
      "billable-ad-spend-detail-modal.dsp_advertisers",
      "billable-ad-spend-detail-modal.advertiser",
      this.data.billingPlan?.dspAdSpendFee?.amount,
      this.data.dspAdSpendFees,
      (fee) => `${fee.dspAdvertiserId}`,
      (fee) => fee.advertiserName || `${fee.dspAdvertiserId}`,
    ),
    toSection(
      "billable-ad-spend-detail-modal.dsp_m19_seat_advertisers",
      "billable-ad-spend-detail-modal.advertiser",
      this.data.billingPlan?.dspM19SeatAdSpendFee?.amount,
      this.data.dspM19SeatAdSpendFees,
      (fee) => `${fee.dspAdvertiserId}`,
      (fee) => fee.advertiserName || `${fee.dspAdvertiserId}`,
    ),
  ].filter((section) => section.rows.length > 0);

  readonly total = this.sections.reduce((total, section) => total + section.adSpendFeeSubtotal, 0);

  exportCsv(): void {
    const t = (key: string) => this.translocoService.translate(key);
    this.csvExportService.exportCsv<AdSpendFeeCsvLine>(
      { prefix: this.data.exportFilePrefix },
      this.sections.flatMap((section) => section.rows.map((row) => ({ section: t(section.titleKey), ...row }))),
      [
        fieldExtractor(t("billable-ad-spend-detail-modal.section"), (line) => line.section),
        fieldExtractor(t("common.account"), (line) => line.name),
        fieldExtractor(t("common.marketplace"), (line) => line.marketplace),
        fieldExtractor(t("billable-ad-spend-detail-modal.total_ad_spend"), (line) => line.adSpend.toFixed(2)),
        fieldExtractor(t("billable-ad-spend-detail-modal.ad_spend_fee"), (line) => line.adSpendFee.toFixed(2)),
      ],
    );
  }
}

function toSection(
  titleKey: string,
  nameColumnKey: string,
  feeRate: number | undefined,
  fees: DailyAdSpendFee[],
  groupKey: (fee: DailyAdSpendFee) => string,
  name: (fee: DailyAdSpendFee) => string,
): AdSpendFeeSection {
  const groups = new Map<string, DailyAdSpendFee[]>();
  for (const fee of fees) {
    groups.set(groupKey(fee), [...(groups.get(groupKey(fee)) ?? []), fee]);
  }
  const rows = Array.from(groups.values())
    .map((groupFees) => ({
      name: name(groupFees[0]),
      marketplace: groupFees[0].marketplace,
      adSpend: groupFees.reduce((total, fee) => total + (fee.spend ?? 0), 0),
      adSpendFee: sumFees(groupFees),
    }))
    .sort((a, b) => b.adSpendFee - a.adSpendFee);
  return {
    titleKey,
    nameColumnKey,
    feeRate,
    rows,
    adSpendSubtotal: rows.reduce((total, row) => total + row.adSpend, 0),
    adSpendFeeSubtotal: sumFees(rows),
  };
}

function sumFees(fees: { adSpendFee: number }[]): number {
  return fees.reduce((total, fee) => total + fee.adSpendFee, 0);
}
